/**
 * PATH: packages/email-hub/src/feature/components/MailboxContextMenu.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-MAILBOX-CONTEXT-MENU-001
 * PURPOSE: Lightweight right-click / kebab context menu for a connected
 *          mailbox row. Renders into a body portal at a cursor/anchor point,
 *          clamped to the viewport, and self-dismisses on outside interaction.
 * WHY: The ConnectedMailboxesPanel lives inside clipped popovers/footers, so
 *      a normal absolutely-positioned menu would be cut off. A portal +
 *      viewport clamp keeps the menu fully visible wherever the panel renders.
 * FLOW:
 * ┌──────────────┐ ┌────────────────────┐ ┌──────────────────────┐
 * │ x,y + items  │ → │ measure + clamp to │ → │ portal menu; click   │
 * │ + onClose    │   │ viewport edges     │   │ runs action + closes │
 * └──────────────┘ └────────────────────┘ └──────────────────────┘
 * DEPENDENCIES:
 * - react-dom createPortal: escape the clipped popover
 * - mailboxMenuItems: MailboxMenuItem shape (icon + onClick)
 * RELATED:
 * - ConnectedMailboxesPanel.tsx: owns open/close state + builds items
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { createPortal } from 'react-dom'
import { cn } from '../../_internal/lib/utils'
import type { MailboxMenuItem } from '../lib/mailboxMenuItems'

const VIEWPORT_PADDING = 8

export type MailboxContextMenuProps = {
  x: number
  y: number
  items: MailboxMenuItem[]
  onClose: () => void
  /** Accessible name for the menu. Defaults to the mailbox-actions label;
   *  reusers (e.g. the card Junk menu) pass their own so the a11y name
   *  matches the surface. */
  ariaLabel?: string
}

export function MailboxContextMenu({
  x,
  y,
  items,
  onClose,
  ariaLabel = 'Mailbox actions',
}: MailboxContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y })

  // Measure after mount, then nudge so the menu never spills off-screen.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    let left = x
    let top = y
    if (left + width > window.innerWidth - VIEWPORT_PADDING) {
      left = Math.max(VIEWPORT_PADDING, window.innerWidth - width - VIEWPORT_PADDING)
    }
    if (top + height > window.innerHeight - VIEWPORT_PADDING) {
      top = Math.max(VIEWPORT_PADDING, window.innerHeight - height - VIEWPORT_PADDING)
    }
    setPos({ left, top })
  }, [x, y])

  // Dismiss on Escape, outside pointer, scroll, or resize.
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(true, onClose, [ref])
  useEffect(() => {
    const onScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Node && ref.current?.contains(target)) return
      const dock = document.getElementById('email-mailboxes-dock-menu')
      if (target instanceof Node && dock?.contains(target)) return
      onClose()
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onClose)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onClose)
    }
  }, [onClose])

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={ariaLabel}
      id="email-mailbox-actions-menu"
      data-quotech-menu-nested=""
      style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 9999 }}
      className="min-w-[200px] max-w-[260px] overflow-hidden rounded-lg border border-border bg-popover py-1 text-xs text-popover-foreground shadow-lg"
    >
      {items.map((item) =>
        item.kind === 'divider' ? (
          <div key={item.id} className="my-1 h-px bg-border" />
        ) : (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return
              item.onClick()
              onClose()
            }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40',
              item.danger && 'text-red-600 dark:text-red-400',
            )}
          >
            {item.icon && <item.icon className="h-3.5 w-3.5 shrink-0 opacity-80" />}
            <span className="truncate">{item.label}</span>
          </button>
        ),
      )}
    </div>,
    document.body,
  )
}
